/** Stats fed to the perf HUD each frame. */
export interface HudStats {
  fps: number;
  frameMs: number;
  drawCalls: number;
  meshes: number;
  posX: number;
  posY: number;
  posZ: number;
  yaw: number; // view yaw (rad)
  speed: number;
  onGround: boolean;
  crouching: boolean;
  locked: boolean;
  connected: boolean;
  entityId: number | null;
  players: number;
  netsim: string;
  hp: number;
  alive: boolean;
}

/** Minimal DOM perf overlay, toggled with F3. */
export class Hud {
  private visible = false;
  private acc = 0;
  private readonly throttleMs = 100;

  constructor(private readonly el: HTMLElement) {}

  toggle(): void {
    this.visible = !this.visible;
    this.el.classList.toggle('hidden', !this.visible);
  }

  update(s: HudStats, dtMs: number): void {
    if (!this.visible) return;
    this.acc += dtMs;
    if (this.acc < this.throttleMs) return;
    this.acc = 0;
    this.el.textContent =
      `FPS        ${s.fps.toFixed(0).padStart(5)}  (${s.frameMs.toFixed(1)} ms)\n` +
      `draw calls ${String(s.drawCalls).padStart(5)}\n` +
      `meshes     ${String(s.meshes).padStart(5)}\n` +
      `pos        ${fmt(s.posX)} ${fmt(s.posY)} ${fmt(s.posZ)}\n` +
      `facing     ${facing(s.yaw)}\n` +
      `speed      ${s.speed.toFixed(2).padStart(5)} u/s\n` +
      `ground     ${s.onGround ? 'yes' : 'no '}   crouch ${s.crouching ? 'yes' : 'no'}\n` +
      `pointer    ${s.locked ? 'locked' : 'free'}\n` +
      `hp         ${String(Math.round(s.hp)).padStart(5)}  ${s.alive ? 'alive' : 'DOWN'}\n` +
      `net        ${s.connected ? 'connected' : 'offline'}  id ${s.entityId ?? '-'}  players ${s.players}\n` +
      `netsim     ${s.netsim}  (F6 to cycle)`;
  }
}

const fmt = (n: number): string => (n >= 0 ? ' ' : '') + n.toFixed(1);

/** Yaw → compass + heading + look vector (yaw 0 = +z = N, π/2 = +x = E). */
function facing(yaw: number): string {
  let deg = (yaw * 180) / Math.PI % 360;
  if (deg < 0) deg += 360;
  const card = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'][Math.round(deg / 45) % 8]!;
  const fx = Math.sin(yaw);
  const fz = Math.cos(yaw);
  return `${card.padStart(2)} ${deg.toFixed(0).padStart(3)}°  look ${fmt(fx)} ${fmt(fz)}`;
}
